/**
 * Le deck — page d'accueil. (Brief §3.3)
 */

import { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { buildTweetUrl, type DeckCard, type HomeSummary } from "@rm/shared";

import { api } from "../lib/api.js";
import { SwipeCard } from "../components/SwipeCard.js";
import { BudgetBar, Empty, Spinner } from "../components/ui.js";
import { DeckActions } from "../components/DeckActions.js";

export function DeckPage() {
  const [cards, setCards] = useState<DeckCard[] | null>(null);
  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exiting, setExiting] = useState<{ id: string; direction: "left" | "right" } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [deck, home] = await Promise.all([
        api.get<{ cards: DeckCard[] }>("/deck"),
        api.get<HomeSummary>("/deck/summary"),
      ]);
      setCards(deck.cards);
      setSummary(home);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible");
      setCards([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Le swipe se fait en deux temps, et c'est volontaire.
   *
   * On marque d'abord la carte comme sortante — ce rendu lui transmet la
   * direction — puis un effet la retire de la liste, ce qui déclenche
   * l'animation de sortie d'AnimatePresence. Retirer et animer dans le même
   * rendu ferait toujours partir la carte du même côté au clic sur un bouton,
   * puisque `x` vaut alors 0.
   */
  const swipe = useCallback((card: DeckCard, direction: "left" | "right") => {
    setExiting({ id: card.tweet_id, direction });

    // Optimiste : l'appel part en parallèle de l'animation. Un échec réseau
    // remet la carte en tête — mieux vaut une carte qui revient qu'un deck qui rame.
    api
      .post("/deck/swipe", { tweet_id: card.tweet_id, direction })
      .then(() => api.get<HomeSummary>("/deck/summary").then(setSummary).catch(() => {}))
      .catch((err) => {
        setCards((current) => [card, ...(current ?? [])]);
        setError(err instanceof Error ? err.message : "Swipe non enregistré");
      });
  }, []);

  /**
   * Blocage d'un compte : le serveur bascule le compte en `muted` et expire
   * toutes ses cartes en attente. On retire donc localement TOUTES ses cartes,
   * pas seulement celle affichée — sinon les suivantes resteraient visibles
   * jusqu'au rechargement.
   */
  const mute = useCallback(async (card: DeckCard) => {
    const authorId = card.author_id;
    try {
      const res = await api.post<{ handle: string; removed: string[] }>("/deck/mute", {
        tweet_id: card.tweet_id,
      });
      setCards((current) =>
        (current ?? []).filter((c) =>
          authorId === null ? c.tweet_id !== card.tweet_id : c.author_id !== authorId,
        ),
      );
      setNotice(
        `@${res.handle} ne réapparaîtra plus` +
          (res.removed.length > 1 ? ` (${res.removed.length} cartes retirées)` : "") +
          ". Réversible dans Réglages → Comptes.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Blocage impossible");
    }
  }, []);

  // Retrait effectif, une fois la direction transmise à la carte.
  useEffect(() => {
    if (!exiting) return;
    setCards((current) => (current ?? []).filter((c) => c.tweet_id !== exiting.id));
  }, [exiting]);

  if (cards === null) return <Spinner />;

  const top = cards[0];
  // On ne monte que trois cartes : au-delà, rien n'est visible sous la pile et
  // chaque carte de plus est un coût de rendu pur.
  const stack = cards.slice(0, 3);

  return (
    <div className="flex h-full flex-col px-4 pt-4">
      {summary && <Header summary={summary} />}

      {error && (
        <p className="mb-3 rounded-lg bg-[#2f1618] px-3 py-2 text-xs text-[var(--color-bad)]">
          {error}
        </p>
      )}

      {notice && (
        <button
          onClick={() => setNotice(null)}
          className="mb-3 w-full rounded-lg bg-[var(--color-surface-2)] px-3 py-2 text-left text-xs text-[var(--color-muted)]"
        >
          {notice}
        </button>
      )}

      {cards.length === 0 ? (
        <Empty
          title="Deck vide"
          hint="Le prochain pull remplira les cartes. Tu peux aussi en lancer un depuis les réglages."
        />
      ) : (
        <>
          {/*
            Répartition de la hauteur restante : quatre cinquièmes pour le deck,
            un cinquième pour les actions. Les deux zones centrent leur contenu,
            si bien qu'un grand écran étale au lieu de laisser un vide en bas, et
            qu'un petit écran comprime sans jamais passer sous la barre
            d'onglets.

            `min-h-0` sur les deux : sans lui, un enfant en `flex-1` refuse de
            passer sous sa taille de contenu et déborde du conteneur.

            Le `pb-7` réserve la place des cartes de la pile, qui dépassent de
            24 px sous celle du dessus.
          */}
          <div className="flex min-h-0 flex-[4] items-center justify-center pb-7">
            <div
              className="relative h-full"
              style={{
                // `height: 100%` + ratio fixe : la largeur découle de la hauteur
                // disponible. `maxWidth` reprend la main sur un écran étroit et
                // haut, et le ratio fait alors redescendre la hauteur.
                aspectRatio: "3 / 4",
                maxWidth: "100%",
                // `perspective` doit vivre sur le parent — sur l'élément
                // lui-même, elle ne produirait aucun effet de fuite.
                perspective: "1200px",
              }}
            >
            <AnimatePresence initial={false}>
              {stack.map((card, index) => (
                <SwipeCard
                  key={card.tweet_id}
                  card={card}
                  depth={index}
                  {...(exiting?.id === card.tweet_id ? { exitTo: exiting.direction } : {})}
                  {...(index === 0
                    ? {
                        onSwipe: (direction: "left" | "right") => swipe(card, direction),
                        onMute: () => mute(card),
                        onOpen: () =>
                          window.open(
                            buildTweetUrl(
                              card.author?.handle ?? card.author_handle ?? "i",
                              card.tweet_id,
                            ),
                            "_blank",
                            "noopener",
                          ),
                      }
                    : {})}
                />
              ))}
            </AnimatePresence>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 items-center justify-center">
            <DeckActions
              disabled={!top}
              onPass={() => top && swipe(top, "left")}
              onKeep={() => top && swipe(top, "right")}
            />
          </div>


        </>
      )}
    </div>
  );
}

function Header({ summary }: { summary: HomeSummary }) {
  const progress = Math.min(1, summary.replies_today / Math.max(1, summary.daily_goal));

  return (
    <header className="mb-4 flex flex-col gap-3">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Aujourd'hui</p>
          <p className="text-2xl font-semibold tabular-nums">
            {summary.replies_today}
            <span className="text-[var(--color-muted)]">/{summary.daily_goal}</span>
            <span className="ml-2 text-sm font-normal text-[var(--color-muted)]">replies</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold tabular-nums">
            {summary.streak > 0 ? `🔥 ${summary.streak}` : "—"}
          </p>
          <p className="text-xs text-[var(--color-muted)] tabular-nums">{summary.points_today} pts</p>
        </div>
      </div>

      <div className="h-1 w-full overflow-hidden rounded-full bg-[var(--color-line)]">
        <div
          className="h-full bg-[var(--color-accent)] transition-all"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      <div className="flex items-center gap-2">
        <BudgetBar ratio={summary.budget.ratio} exhausted={summary.budget.exhausted} />
        <span className="shrink-0 text-[11px] tabular-nums text-[var(--color-muted)]">
          {summary.budget.spent_usd.toFixed(2)} / {summary.budget.cap_usd.toFixed(2)} $
        </span>
      </div>

      {summary.budget.exhausted && (
        <p className="rounded-lg bg-[#2a2410] px-3 py-2 text-xs text-[var(--color-warn)]">
          Budget du jour atteint. Aucun nouvel appel API jusqu'à demain — l'app sert le cache.
        </p>
      )}
    </header>
  );
}
